import Image from 'next/image';
import Link from 'next/link';
import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useTrackClickedPublicBoardLink } from '@air/analytics';
import { ChevronsLeftIcon } from '@air/design-icons';

import { AssetModalHeaderAirLogoLink } from '~/components/AssetModalV2/Header/components/AssetModalHeaderAirLogoLink';
import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { AccountInformation } from '~/components/v2/components/shared/account/AccountInformation';
import { createAllContentAssetRoute, createBoardClipRoute } from '~/constants/routes';
import { usePublicCustomLogo } from '~/hooks/usePublicCustomLogo';
import { useShowPublicAirLogo } from '~/hooks/useShowPublicAirLogo';
import { useAccountContext } from '~/providers/AccountProvider';
import { centralizedClipBoardContextSelector, centralizedClipSelector } from '~/store/centralizedClip/selectors';

export const LoggedInBanner = memo(() => {
  const { workspaceImage, workspaceName } = usePublicWorkspace();
  const { customLogoUrl } = usePublicCustomLogo();
  const clip = useSelector(centralizedClipSelector);
  const boardContext = useSelector(centralizedClipBoardContextSelector);
  const { trackClickedPublicBoardLink } = useTrackClickedPublicBoardLink();
  const { data: account } = useAccountContext();
  const { showAirLogo } = useShowPublicAirLogo();
  const href = boardContext
    ? createBoardClipRoute({ clipId: clip.id, boardTitle: '', boardId: boardContext })
    : createAllContentAssetRoute(clip.id);

  const onClick = useCallback(() => {
    trackClickedPublicBoardLink({
      name: 'clicked view in workspace',
      page_area: 'banner',
      page_type: 'public asset',
    });
  }, [trackClickedPublicBoardLink]);

  return (
    <div
      className="relative flex size-full h-12 items-center justify-between gap-8 ads:border-ads-gray-5 border-gray-5 border-b ads:bg-ads-gray-4 bg-gray-4 px-4"
      data-testid="PUBLIC_ASSET_LOGGED_IN_BANNER"
    >
      <Link
        href={href}
        className="flex cursor-pointer items-center justify-start ads:text-primary text-gray-12 ads:hover:text-primary hover:text-gray-12"
        data-testid="PUBLIC_ASSET_WORKSPACE_INFO"
        onClick={onClick}
      >
        <ChevronsLeftIcon size="xs" className="shrink-0 ads:text-icon-default text-gray-10" />
        <div className="flex items-center gap-2">
          {customLogoUrl ? (
            // Custom brand marks are frequently wide (horizontal wordmarks), so the logo renders
            // whole at the banner's logo height with auto width — no corner rounding — instead of
            // cropped into the square avatar box (the workspace-image branch below keeps the square
            // avatar rendering). max-w bracket is a proto-fidelity cap
            // (mattq/proto-presentation-mode) — no configured max-w steps exist near it.
            // `unoptimized` skips the next/image resizer, whose fixed-size renditions would soften
            // wide marks scaled by CSS; height/width are only the intrinsic-ratio hint.
            <Image
              alt={`Logo for ${workspaceName}`}
              className="h-6 w-auto max-w-[120px] shrink-0 object-contain object-left"
              height={24}
              src={customLogoUrl}
              unoptimized
              width={144}
            />
          ) : (
            !!workspaceImage && (
              <div className="relative size-6 overflow-hidden rounded-4">
                <Image
                  alt={`Logo for ${workspaceName}`}
                  className="object-cover"
                  fill
                  sizes="24px"
                  src={workspaceImage}
                />
              </div>
            )
          )}
          <div className="truncate ads:text-primary text-12 text-gray-12">
            View in <span className="font-semibold">{workspaceName}</span>
          </div>
        </div>
      </Link>
      <div className="flex shrink-0 items-center gap-4">
        {!!account && <AccountInformation />}

        {showAirLogo && (
          <>
            <div className="h-4 w-px ads:bg-ads-gray-5 bg-gray-5" />
            <AssetModalHeaderAirLogoLink />
          </>
        )}
      </div>
    </div>
  );
});

LoggedInBanner.displayName = 'LoggedInBanner';
